import Vue from "vue";
import VueRouter from "vue-router";
import store from "../store";
import {
  USER_CRM_REQUEST,
  USER_KNOWLEDGEBASE_REQUEST,
  USER_SET_ROUTE,
  USER_CRM_LICENCE_STATUS_REQUEST,
  USER_CRM_RELOAD,
} from "@/store/actions/user";

import restrictions from "@/utils/restrictions";

Vue.use(VueRouter);

const Login = () => import("../views/general/Login.vue");
const Register = () => import("../views/general/Register.vue");
const Items = () => import("../views/builder/Items.vue");
const ItemsDetails = () => import("../views/builder/ItemsDetails.vue");
const ItemsAddEdit = () => import("../views/builder/ItemsAddEdit.vue");
const Documents = () => import("../views/builder/Documents.vue");
const DocumentsDetails = () => import("../views/builder/DocumentsDetails.vue");
const DocumentsAddEdit = () => import("../views/builder/DocumentsAddEdit.vue");
const Models = () => import("../views/builder/Models.vue");
const ModelsDetails = () => import("../views/builder/ModelsDetails.vue");
const ModelsAddEdit = () => import("../views/builder/ModelsAddEdit.vue");
const Boats = () => import("../views/builder/Boats.vue");
const BoatsDetails = () => import("../views/builder/BoatsDetails.vue");
const BoatsAddEdit = () => import("../views/builder/BoatsAddEdit.vue");
const Messages = () => import("../views/messages/Messages.vue");
const MessageThread = () => import("../views/messages/MessageThread.vue");
const Brands = () => import("../views/users/Brands.vue");
const BrandsAddEdit = () => import("../views/users/BrandsAddEdit.vue");
const Dealers = () => import("../views/users/Dealers.vue");
const DealersAddEdit = () => import("../views/users/DealersAddEdit.vue");
const DealersDetails = () => import("../views/users/DealersDetails.vue");
const ServiceProviders = () => import("../views/users/ServiceProviders.vue");
const ServiceProvidersAddEdit = () =>
  import("../views/users/ServiceProvidersAddEdit.vue");
const ServiceProvidersDetails = () =>
  import("../views/users/ServiceProvidersDetails.vue");
const Dashboards = () => import("../views/dashboards/Dashboards.vue");
const Dashboard = () => import("../views/dashboards/Dashboard.vue");
const Home = () => import("../views/general/Home.vue");
const Builder = () => import("../views/builder/Builder.vue");
const Users = () => import("../views/users/Users.vue");
const NoPermission = () => import("../views/general/NoPermission.vue");
const NotFound = () => import("../views/general/NotFound.vue");
const NewUser = () => import("../views/general/NewUser.vue");
const ResetPassword = () => import("../views/general/ResetPassword.vue");
const ResetPasswordRequest = () =>
  import("../views/general/ResetPasswordRequest.vue");
const Data = () => import("../views/data/Data.vue");
const SharedTask = () => import("../components/miscellaneous/SharedTask");
const GroupsEdit = () => import("../views/users/GroupsEdit.vue");
const Tasks = () => import("../views/tasks/Tasks.vue");
const TasksDetails = () => import("../views/tasks/TasksDetails.vue");
const TasksAddEdit = () => import("../views/tasks/TasksAddEdit.vue");
const ExternalAuth = () => import("../views/general/ExternalAuth");
const Maintenance = () => import("../views/tasks/Maintenance.vue");
const Services = () => import("../views/tasks/Services.vue");
const TasksTemplates = () => import("../views/tasks/TasksTemplates.vue");
const TasksTemplatesAddEdit = () =>
  import("../views/tasks/TasksTemplatesAddEdit.vue");
const Consents = () => import("../views/consents/Consents.vue");
const TermsOfService = () => import("../views/consents/TermsOfService.vue");
const NewsFeedList = () => import("../views/newsfeed/NewsFeedList.vue");
const NewsFeedAddEdit = () => import("../views/newsfeed/NewsFeedAddEdit.vue");
const NewsFeedDetails = () => import("../views/newsfeed/NewsFeedDetails.vue");
const Account = () => import("../views/general/Account.vue");
const Legal = () => import("../views/general/Legal.vue");
const Notifications = () => import("../views/notifications/Notifications.vue");
const UnderMaintenance = () => import("../views/general/UnderMaintenance.vue");
const Billings = () => import("../views/general/Billings.vue");
const Billing = () => import("../views/general/Billing.vue");
const Accounts = () => import("../views/general/Accounts.vue");

const routes = [
  {
    name: "base",
    path: "",
    component: Home,
    meta: { visible: "dashboards", requiresAuth: true },
    children: [
      {
        path: "/builder",
        component: Builder,
        name: "builder",
        redirect: { name: "boats" },
        meta: { visible: "boats" },
        children: [
          {
            path: "/builder/items/",
            component: Items,
            name: "items",
            meta: { visible: "items", menu: "items" },
          },
          {
            path: "/builder/items/new/",
            component: ItemsAddEdit,
            name: "itemsAdd",
            meta: { visible: "items", menu: "items" },
          },
          {
            path: "/builder/items/:id/",
            component: ItemsDetails,
            name: "itemsDetails",
            meta: { visible: "items", menu: "items" },
          },
          {
            path: "/builder/items/:id/edit",
            component: ItemsAddEdit,
            name: "itemsEdit",
            meta: { visible: "items", menu: "items" },
          },
          {
            path: "/builder/documents/",
            component: Documents,
            name: "documents",
            meta: { visible: "documents", menu: "documents" },
          },
          {
            path: "/builder/documents/new",
            component: DocumentsAddEdit,
            name: "documentsAdd",
            meta: { visible: "documents", menu: "documents" },
          },
          {
            path: "/builder/documents/:id/",
            component: DocumentsDetails,
            name: "documentsDetails",
            meta: { visible: "documents", menu: "documents" },
          },
          {
            path: "/builder/documents/:id/edit",
            component: DocumentsAddEdit,
            name: "documentsEdit",
            meta: { visible: "documents", menu: "documents" },
          },
          {
            path: "/builder/models/",
            component: Models,
            name: "models",
            meta: { visible: "models", menu: "models" },
          },
          {
            path: "/builder/models/new/",
            component: ModelsAddEdit,
            name: "modelsNew",
            meta: { visible: "models", menu: "models" },
          },
          {
            path: "/builder/models/:id/",
            component: ModelsDetails,
            name: "modelsDetails",
            meta: { visible: "models", menu: "models" },
          },
          {
            path: "/builder/models/:id/edit/",
            component: ModelsAddEdit,
            name: "modelsEdit",
            meta: { visible: "models", menu: "models" },
          },
          {
            path: "/builder/boats/new/",
            component: BoatsAddEdit,
            name: "boatsNew",
            meta: { visible: "boats", menu: "boats" },
          },
          {
            path: "/builder/boats/:id/",
            component: BoatsDetails,
            name: "boatsDetails",
            meta: { visible: "boats", menu: "boats" },
          },
          {
            path: "/builder/boats/",
            component: Boats,
            name: "boats",
            meta: { visible: "boats", menu: "boats" },
          },
          {
            path: "/builder/boats/:id/edit/",
            component: BoatsAddEdit,
            name: "boatsEdit",
            meta: { visible: "boats", menu: "boats" },
          },
        ],
      },
      {
        name: "data",
        path: "/data",
        component: Data,
        meta: { visible: "data" },
        // children: [
        //   {
        //     path: ":id/:datefrom&:dateto",
        //     component: Data,
        //     name: "libyaCharts",
        //     meta: { visible: "data" },
        //   },
        // ],
      },
      {
        name: "task",
        path: "/tasks",
        component: Maintenance,
        meta: { visible: "tasks" },
        children: [
          {
            path: "/",
            name: "tasks",
            component: Tasks,
            meta: { visible: "tasks", menu: "tasks" },
          },
          {
            path: "new",
            component: TasksAddEdit,
            name: "tasksNew",
            meta: { visible: "tasks", menu: "tasks" },
          },
          {
            path: ":id/",
            component: TasksDetails,
            name: "tasksDetails",
            meta: { visible: "tasks", menu: "tasks" },
          },
          {
            path: ":id/edit",
            component: TasksAddEdit,
            name: "tasksEdit",
            meta: { visible: "tasks", menu: "tasks" },
          },
          {
            path: "/services",
            name: "services",
            component: Services,
            meta: { visible: "services", menu: "tasksMaintenance" },
          },
          {
            path: "/templates",
            name: "tasksTemplate",
            component: TasksTemplates,
            meta: { visible: "services", menu: "tasksTemplates" },
          },
          {
            path: "/templates/:id/edit",
            component: TasksTemplatesAddEdit,
            name: "tasksTemplatesEdit",
            meta: { visible: "tasks", menu: "tasksTemplates" },
          },
          {
            path: "/templates/new",
            component: TasksTemplatesAddEdit,
            name: "tasksTemplatesNew",
            meta: { visible: "tasks", menu: "tasksTemplates" },
          },
        ],
      },
      {
        name: "users",
        path: "/users",
        redirect: { name: "brands" },
        component: Users,
        children: [
          {
            path: "/users/brands/:brandId/:tab/",
            component: GroupsEdit,
            name: "groups",
            meta: { visible: "groups" },
          },
          {
            path: "/users/brands/:id/subgroup",
            component: BrandsAddEdit,
            name: "subgroupBrand",
            meta: { visible: "groups" },
          },
          {
            path: "/users/brands",
            component: Brands,
            name: "brands",
            meta: { visible: "groups" },
          },
          {
            path: "/users/dealers/new/",
            component: DealersAddEdit,
            name: "dealerNew",
            meta: { visible: "dealers" },
          },
          {
            path: "/users/dealers/",
            component: Dealers,
            name: "dealers",
            meta: { visible: "dealers" },
          },
          {
            path: "/users/dealers/:id/edit/",
            component: DealersAddEdit,
            name: "dealerEdit",
            meta: { visible: "dealers" },
          },
          {
            path: "/users/dealers/:id/",
            component: DealersDetails,
            name: "dealerDetails",
            meta: { visible: "dealers" },
          },
          {
            path: "/users/providers",
            component: ServiceProviders,
            name: "serviceProviders",
            meta: { visible: "service_providers" },
          },
          {
            path: "/users/providers/:id/:tab/edit/",
            component: ServiceProvidersAddEdit,
            name: "serviceProviderEdit",
            meta: { visible: "service_providers" },
          },
          {
            path: "/users/providers/:tab/new/",
            component: ServiceProvidersAddEdit,
            name: "serviceProviderNew",
            meta: { visible: "service_providers" },
          },
          {
            path: "/users/providers/:id/",
            component: ServiceProvidersDetails,
            name: "serviceProviderDetails",
            meta: { visible: "service_providers" },
          },
        ],
      },
      {
        name: "messages",
        path: "/messages",
        component: Messages,
        meta: { visible: "messages" },
        children: [
          {
            path: ":id",
            component: MessageThread,
            name: "messages-thread",
            meta: { visible: "messages" },
          },
        ],
      },

      {
        name: "notifications",
        path: "/notifications",
        component: Notifications,
        meta: { visible: "notifications" },
      },

      {
        name: "dashboards",
        path: "/dashboards",
        component: Dashboards,
        meta: { visible: "dashboards" },
        children: [
          {
            path: ":id",
            component: Dashboard,
            name: "dashboard",
            meta: { visible: "dashboards" },
          },
        ],
      },
      {
        name: "new-user",
        path: "/newuser",
        component: NewUser,
      },
      {
        name: "newsfeed",
        path: "/news-feed",
        component: NewsFeedList,
        meta: { visible: "newsfeed" },
      },
      {
        path: "/news-feed/new",
        component: NewsFeedAddEdit,
        name: "newsFeedNew",
        meta: { visible: "newsfeed" },
      },
      {
        path: "/news-feed/:id/edit",
        component: NewsFeedAddEdit,
        name: "newsFeedEdit",
        meta: { visible: "newsfeed" },
      },
      {
        path: "/news-feed/:id",
        component: NewsFeedDetails,
        name: "newsFeedDetails",
        meta: { visible: "newsfeed" },
      },
      {
        name: "account",
        path: "/account",
        component: Account,
        children: [
          {
            path: "/account/billings/",
            component: Billings,
            name: "billings",
          },
          {
            path: "/account/billings/:id/",
            component: Billing,
            name: "billing",
          },
          {
            path: "/account/legal/",
            component: Legal,
            name: "legal",
          },
          {
            path: "/account/new/",
            component: DealersAddEdit,
            name: "partnerNew",
            meta: { visible: "dealers" },
          },
          {
            path: "/account/",
            component: Accounts,
            name: "partners",
            meta: { visible: "dealers" },
          },
          {
            path: "/account/:id/edit/",
            component: DealersAddEdit,
            name: "partnerEdit",
            meta: { visible: "dealers" },
          },
          {
            path: "/account/:id/",
            component: DealersDetails,
            name: "partnerDetails",
            meta: { visible: "dealers" },
          },
        ],
      },
    ],
  },
  {
    path: "*",
    name: "notFound",
    component: NotFound,
  },
  {
    path: "/consents",
    name: "consents",
    component: Consents,
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: "/terms-of-service",
    name: "termsOfService",
    component: TermsOfService,
    meta: { requiresAuth: true },
  },
  {
    path: "/no-permission",
    name: "noPermission",
    component: NoPermission,
  },
  {
    path: "/shared-tasks/:id",
    name: "sharedTask",
    component: SharedTask,
  },
  {
    path: "/login",
    name: "login",
    component: Login,
  },
  {
    path: "/external-auth",
    name: "externalAuth",
    component: ExternalAuth,
  },
  {
    path: "/reset-password-request",
    name: "resetPasswordRequest",
    component: ResetPasswordRequest,
  },
  {
    path: "/reset-password/:uuid",
    name: "resetPassword",
    component: ResetPassword,
  },
  {
    path: "/register/:uuid",
    name: "register",
    component: Register,
  },
  {
    path: "/under-maintenance",
    name: "underMaintenance",
    component: UnderMaintenance,
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior(to, from, savedPosition) {
    return { x: 0, y: 0 };
  },
  routes,
});

router.pushAsync = function (route) {
  return new Promise((resolve, reject) => {
    router.push(route, resolve, reject);
  });
};

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject)
    return originalPush.call(this, location, onResolve, onReject);
  return originalPush.call(this, location).catch((err) => {
    if (VueRouter.isNavigationFailure(err)) {
      // resolve err
      return err;
    }
    // rethrow error
    return Promise.reject(err);
  });
};

const originalReplace = VueRouter.prototype.replace;
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject)
    return originalReplace.call(this, location, onResolve, onReject);
  return originalReplace.call(this, location).catch((err) => {
    if (VueRouter.isNavigationFailure(err)) {
      // resolve err
      return err;
    }
    // rethrow error
    return Promise.reject(err);
  });
};

router.beforeEach((to, from, next) => {
  store.commit("USER_SET_ROUTE", to);

  if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    !store.getters.isAuthenticated
  ) {
    if (to.matched.length !== 0) {
      next({ name: "login", query: { next: to.fullPath } });
    } else {
      next({ name: "login" });
    }
  } else if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    store.getters.isAuthenticated
  ) {
    let promises = [];

    if (
      store.getters.getKnowledgeBase === null ||
      typeof store.getters.getKnowledgeBase.error !== "undefined"
    ) {
      promises.push(store.dispatch(USER_KNOWLEDGEBASE_REQUEST));
    } else {
      promises.push(
        new Promise((resolve, reject) => {
          resolve(store.getters.getKnowledgeBase);
        })
      );
    }

    if (
      Object.keys(store.getters.getMe).length === 0 &&
      !store.getters.getCurrentLicence.fetched
    ) {
      promises.push(store.dispatch(USER_CRM_RELOAD));
    } else if (
      Object.keys(store.getters.getMe).length === 0 &&
      store.getters.getCurrentLicence.fetched
    ) {
      promises.push(store.dispatch(USER_CRM_REQUEST));
    } else if (
      Object.keys(store.getters.getMe).length !== 0 &&
      !store.getters.getCurrentLicence.fetched
    ) {
      promises.push(store.dispatch(USER_CRM_RELOAD));
      //NOTE: No need to be async
    } else {
      promises.push(
        new Promise((resolve, reject) => {
          resolve(store.getters.getMe);
        })
      );
      promises.push(
        new Promise((resolve, reject) => {
          resolve(store.getters.getCurrentLicence);
        })
      );
    }

    // if (store.getters.getCurrentLicence.fetched === false) {
    //   promises.push(store.dispatch(USER_CRM_LICENCE_STATUS_REQUEST));
    // } else {
    //   //NOTE: No need to be async
    //   promises.push(
    //     new Promise((resolve, reject) => {
    //       resolve(store.getters.getCurrentLicence);
    //     })
    //   );
    // }

    Promise.all(promises).then((values) => {
      if (
        to.name === "termsOfService" ||
        to.name === "consents" ||
        to.name === "billing"
      ) {
        next();
        return;
      } else if (
        store.getters.getIsLicenceStatusNeeded &&
        store.getters.getHasCurrentLicenceStatusRestrictedAccess &&
        to.name !== "billings"
      ) {
        next({ name: "billings" });
        return;
      } else if (to.name === "base") {
        const nextRoute = restrictions.nextBaseRoute(store.getters.getMe);
        next(nextRoute);
        return;
      }

      next();
      return;
    });
  } else {
    next();
  }
});

export default router;
